import React, { useState, useEffect, useCallback, useRef } from 'react';
import AiImageEditorButton from './AiImageEditorButton';
import AiImageEditorModal from './AiImageEditorModal';
import useAiImageChat from './useAiImageChat';
import { uploadAsProductPhoto } from './uploadGeneratedImage';

/**
 * Container component that manages the AI Image Editor
 * Handles:
 * - Modal open/close state
 * - Chat persistence to Firestore via real-time listener
 * - "Replace Main" functionality
 */
function AiImageEditorContainer({ product }) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isReplacing, setIsReplacing] = useState(false);
  
  // Get product data
  const photo = product?.get ? product.get('photo') : product?.photo;
  const shopId = product?.get ? product.get('shopId') : product?.shopId;
  
  // Store the product ref for the real-time listener
  const productRef = product?.ref;
  
  // Ref to store the latest chat history from Firestore (for use in handleChatUpdate)
  const latestChatsRef = useRef([]);
  
  // Handle chat updates - persist to Firestore
  const handleChatUpdate = useCallback(async (updatedChat) => {
    if (!productRef) return;
    
    try {
      // Use the latest chats from the real-time listener
      const existingChats = latestChatsRef.current || [];
      
      // Update or add the chat
      const chatIndex = existingChats.findIndex(c => c.id === updatedChat.id);
      let newChats;
      
      // Prepare the chat for Firestore (strip base64, keep timestamps)
      const chatForFirestore = {
        ...updatedChat,
        createdAt: updatedChat.createdAt,
        messages: updatedChat.messages.map(msg => {
          // Don't store base64 in Firestore - use imageUrl instead
          const { imageBase64, ...msgWithoutBase64 } = msg;
          return {
            ...msgWithoutBase64,
            timestamp: msg.timestamp,
          };
        }),
      };
      
      if (chatIndex >= 0) {
        newChats = [...existingChats];
        newChats[chatIndex] = chatForFirestore;
      } else {
        newChats = [...existingChats, chatForFirestore];
      }
      
      // Update Firestore
      await productRef.update({
        aiImageChats: newChats,
      });
    } catch (error) {
      console.error('Failed to persist chat:', error);
    }
  }, [productRef]);
  
  // Initialize the chat hook
  const {
    currentChat,
    chatHistory,
    isLoading,
    error,
    startNewChat,
    selectChat,
    sendMessage,
    setChatHistory,
  } = useAiImageChat(product, handleChatUpdate);
  
  // Track if we've started a new chat for this modal session
  const hasStartedChatRef = useRef(false);
  
  // Set up real-time Firestore listener when modal opens
  useEffect(() => {
    if (!isModalOpen || !productRef) return;
    
    // Subscribe to real-time updates
    const unsubscribe = productRef.onSnapshot(
      (snapshot) => {
        if (snapshot.exists) {
          const data = snapshot.data();
          const aiImageChats = data?.aiImageChats || [];
          
          // Update the ref for handleChatUpdate to use
          latestChatsRef.current = aiImageChats;
          
          // Update the hook's chat history state
          setChatHistory(aiImageChats);
        }
      },
      (error) => {
        console.error('Error listening to product updates:', error);
      }
    );
    
    // Start a new chat session on first open
    if (!hasStartedChatRef.current) {
      hasStartedChatRef.current = true;
      startNewChat();
    }
    
    // Cleanup listener when modal closes
    return () => {
      unsubscribe();
    };
  }, [isModalOpen, productRef, setChatHistory, startNewChat]);
  
  // Reset the started chat flag when modal closes
  useEffect(() => {
    if (!isModalOpen) {
      hasStartedChatRef.current = false;
    }
  }, [isModalOpen]);
  
  // Handle opening the modal
  const handleOpenModal = () => {
    setIsModalOpen(true);
  };
  
  // Handle closing the modal
  const handleCloseModal = () => {
    setIsModalOpen(false);
  };
  
  // Handle "Replace Main" - swap generated image with main product photo
  const handleReplaceMain = useCallback(async (message) => {
    if (!product?.ref || !message.imageUrl) return;
    
    setIsReplacing(true);
    
    try {
      const oldPhoto = product.get('photo');
      
      // If the image is a base64 data URL, we need to upload it first
      let newPhoto;
      
      if (message.imageUrl.startsWith('data:')) {
        // Extract base64 data and mime type
        const matches = message.imageUrl.match(/^data:([^;]+);base64,(.+)$/);
        if (!matches) {
          throw new Error('Invalid image data URL');
        }
        const [, mimeType, base64Data] = matches;
        
        // Upload and get photo object
        const result = await uploadAsProductPhoto(base64Data, mimeType, shopId);
        newPhoto = result.photo;
      } else if (message.storagePath) {
        // Already uploaded to storage, create photo object from storage path
        const pathParts = message.storagePath.split('/');
        const filename = pathParts[pathParts.length - 1];
        const [id, ext] = filename.split('.');
        
        newPhoto = {
          type: 'g',
          s: shopId,
          id: id,
          e: ext,
          url: message.imageUrl,
        };
      } else {
        throw new Error('No valid image source found');
      }
      
      // Prepare the update
      const update = {
        photo: newPhoto,
      };
      
      // If there's an existing photo, save it to otherPhotos
      if (oldPhoto && oldPhoto.id) {
        update[`otherPhotos.${oldPhoto.id}`] = oldPhoto;
      }
      
      // Update the product
      await product.ref.update(update);
      
      alert('Main photo replaced successfully!');
    } catch (error) {
      console.error('Failed to replace main photo:', error);
      alert('Failed to replace photo: ' + error.message);
    } finally {
      setIsReplacing(false);
    }
  }, [product, shopId]);
  
  // Check if product has a photo
  const hasPhoto = photo && photo.id;
  
  return (
    <>
      <AiImageEditorButton
        onClick={handleOpenModal}
        disabled={!hasPhoto && !product}
      />
      
      <AiImageEditorModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        product={product}
        currentChat={currentChat}
        chatHistory={chatHistory}
        onSendMessage={sendMessage}
        onNewChat={startNewChat}
        onSelectChat={selectChat}
        onReplaceMain={handleReplaceMain}
        isLoading={isLoading || isReplacing}
      />
      
      {error && (
        <div className="text-red-500 text-xs mt-1">
          {error}
        </div>
      )}
    </>
  );
}

export default AiImageEditorContainer;
